import type { components } from '@/generated/bff';

export type BffErrorCode = components['schemas']['BffError']['error'] | 'network';

/**
 * Every failed call becomes one AppError, whatever went wrong: the BFF envelope,
 * a non-envelope response (e.g. Fortify's own 423) or no response at all.
 * `requestId` is shown to users so support can find the request.
 */
export class AppError extends Error {
    readonly status: number;
    readonly code: BffErrorCode;
    readonly requestId: string | undefined;
    readonly details: Record<string, string[]>;
    readonly retryAfter: number | undefined;

    constructor(init: {
        status: number;
        code: BffErrorCode;
        message: string;
        requestId?: string | undefined;
        details?: Record<string, string[]> | undefined;
        retryAfter?: number | undefined;
    }) {
        super(init.message);
        this.name = 'AppError';
        this.status = init.status;
        this.code = init.code;
        this.requestId = init.requestId;
        this.details = init.details ?? {};
        this.retryAfter = init.retryAfter;
    }

    /** The first message for a form field, if the server rejected it. */
    fieldError(field: string): string | undefined {
        return this.details[field]?.[0];
    }

    /**
     * @param parsed The error body when the caller already read it (openapi-fetch
     *               consumes the response), otherwise it is read here.
     */
    static async fromResponse(response: Response, parsed?: unknown): Promise<AppError> {
        const raw: unknown =
            parsed !== undefined
                ? parsed
                : await response
                      .clone()
                      .json()
                      .then((value: unknown) => value)
                      .catch(() => null);
        const body =
            typeof raw === 'object' && raw !== null ? (raw as Partial<components['schemas']['BffError']>) : null;

        return new AppError({
            status: response.status,
            code: body?.error ?? codeForStatus(response.status),
            message: body?.message ?? 'Something went wrong.',
            requestId: body?.request_id ?? response.headers.get('X-Request-Id') ?? undefined,
            details: body?.details,
            retryAfter: body?.retry_after,
        });
    }

    static network(): AppError {
        return new AppError({
            status: 0,
            code: 'network',
            message: 'We could not reach CIBA. Check your connection and try again.',
        });
    }
}

function codeForStatus(status: number): BffErrorCode {
    switch (status) {
        case 401:
            return 'unauthenticated';
        case 403:
            return 'forbidden';
        case 404:
            return 'not_found';
        case 419:
            return 'csrf_mismatch';
        case 422:
            return 'validation_failed';
        case 423:
            return 'password_confirmation_required';
        case 429:
            return 'rate_limited';
        case 503:
            return 'maintenance';
        default:
            return 'server_error';
    }
}
